import { useState } from 'react';
import { X, AlertTriangle, Lock } from 'lucide-react';

interface AnularModalProps {
  titulo: string;
  descripcion: string;
  textoBoton: string;
  onClose: () => void;
  onConfirmar: (clave: string, motivo: string) => Promise<void>;
}

// Modal generico para acciones de "anular" (pago de mantenimiento, credito antiguo, etc):
// siempre pide motivo + clave de administrador, y siempre queda en rojo en Auditoria del
// lado del backend. Se reutiliza para no repetir el mismo formulario en cada pantalla.
const AnularModal = ({ titulo, descripcion, textoBoton, onClose, onConfirmar }: AnularModalProps) => {
  const [clave, setClave] = useState('');
  const [motivo, setMotivo] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [errorLocal, setErrorLocal] = useState('');

  const confirmar = async () => {
    if (!motivo.trim()) {
      setErrorLocal('Ingresa el motivo de la anulación');
      return;
    }
    if (!clave.trim()) {
      setErrorLocal('Ingresa la clave de administrador');
      return;
    }
    setErrorLocal('');
    setProcesando(true);
    try {
      await onConfirmar(clave.trim(), motivo.trim());
    } catch (error: any) {
      setErrorLocal(error.message || 'Error al anular');
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4"
    >
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-red-700">
            <AlertTriangle size={20} />
            {titulo}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            {descripcion}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Motivo de la anulación</label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
              rows={2}
              placeholder="Ej: se registró por error, monto duplicado..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-1">
              <Lock size={14} /> Clave de administrador
            </label>
            <input
              type="password"
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="****"
            />
          </div>

          {errorLocal && (
            <p className="text-sm text-red-600">{errorLocal}</p>
          )}

          <button
            onClick={confirmar}
            disabled={procesando}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <AlertTriangle size={18} />
            {procesando ? 'Anulando...' : textoBoton}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnularModal;
